import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — Energía Imparable

Última actualización: Abril 2026

1. Información que Recopilamos
Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; todos los pagos son procesados por plataformas de pago seguras de terceros.

2. Uso de la Información
Utilizamos tu información para:
• Procesar y entregar tu compra.
• Enviarte actualizaciones relacionadas con tu producto.
• Mejorar nuestros productos y servicios.
No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.

3. Cookies y Tecnologías de Seguimiento
Nuestro sitio puede utilizar cookies y tecnologías similares para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies.

4. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.

5. Tus Derechos
Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, envíanos un mensaje a través de nuestros canales oficiales.

6. Cambios a esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
Términos y Condiciones — Energía Imparable

Última actualización: Abril 2026

1. Aceptación de los Términos
Al acceder a este sitio web y/o realizar una compra, aceptas estos Términos y Condiciones en su totalidad. Si no estás de acuerdo, por favor no utilices este sitio.

2. Productos Digitales
Todos los productos ofrecidos son materiales digitales (ebooks, audios, guías). Una vez completada la compra, recibirás acceso inmediato al contenido. Los productos son para uso personal y no pueden ser redistribuidos, revendidos ni compartidos.

3. Garantía de Devolución de 30 Días
Ofrecemos una garantía de satisfacción de 30 días. Si no estás satisfecho con tu compra por cualquier motivo, puedes solicitar un reembolso completo dentro de los 30 días posteriores a la fecha de compra. El material descargado es tuyo sin importar el reembolso.

4. Descargo de Responsabilidad Médica
El contenido proporcionado es únicamente con fines educativos e informativos. No constituye consejo médico profesional. Siempre consulta con un profesional de la salud antes de realizar cambios en tu dieta, rutina de ejercicios o estilo de vida. Los resultados individuales pueden variar.

5. Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos, logotipos y materiales descargables, es propiedad de Energía Imparable y está protegido por las leyes de propiedad intelectual aplicables.

6. Limitación de Responsabilidad
En ningún caso seremos responsables por daños directos, indirectos, incidentales o consecuentes que resulten del uso de nuestros productos o sitio web.

7. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del sitio después de cualquier cambio constituye la aceptación de los nuevos términos.
`.trim();
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-43">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25
    }} className="bg-brand-card border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-44">
        <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-45">
          <h2 className="font-bebas text-2xl md:text-3xl text-white" data-id="element-46">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors text-zinc-400 hover:text-white" aria-label="Cerrar" data-id="element-47">
            <X className="w-5 h-5" data-id="element-48" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-49">
          <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line" data-id="element-50">
            {content}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 text-center border-t border-white/5 bg-black" data-id="element-51">
        <div className="flex items-center justify-center gap-4 mb-4" data-id="element-52">
          <button onClick={() => setActiveModal('privacy')} className="text-zinc-500 hover:text-brand-orange text-sm transition-colors underline underline-offset-2" data-id="element-53">
            Política de Privacidad
          </button>
          <span className="text-zinc-700" data-id="element-54">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-zinc-500 hover:text-brand-orange text-sm transition-colors underline underline-offset-2" data-id="element-55">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-zinc-600 text-sm" data-id="element-56">
          © {new Date().getFullYear()} Energía Imparable. Todos los derechos
          reservados.
        </p>
      </footer>

      <AnimatePresence data-id="element-57">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-58" />}
      </AnimatePresence>
    </>;
}